'use client';

import { useState, useCallback, FormEvent } from 'react';

import {
  ContactMethod,
  PhoneResponsePreference,
  isValidEmail,
  isValidPhone,
} from '@j-s/dr-shared';
import { isProduction } from '@j-s/helpers';

import { getAppCheckToken } from '../../firebase/config';
import {
  sendContactMessage,
  SendContactMessageResult,
} from '../services/contactMessageService';

export type AppCheckStatus = 'idle' | 'checking' | 'verified' | 'failed';

export function useContactForm() {
  const [name, setName] = useState('');
  const [contactMethod, setContactMethod] = useState<ContactMethod>('email');
  const [phoneResponsePreference, setPhoneResponsePreference] =
    useState<PhoneResponsePreference>('call');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [terms, setTerms] = useState(false);
  const [notifyPreferences, setNotifyPreferences] = useState({
    email: true,
    phone: true,
  });

  const [appCheckStatus, setAppCheckStatus] = useState<AppCheckStatus>(
    isProduction ? 'idle' : 'verified'
  );
  const [appCheckToken, setAppCheckToken] = useState<string | null>(
    isProduction ? null : 'dev-local-token'
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdTicket, setCreatedTicket] =
    useState<SendContactMessageResult | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const verifyAppCheck = useCallback(async (): Promise<string | null> => {
    if (!isProduction) {
      setAppCheckStatus('verified');
      setAppCheckToken('dev-local-token');
      setErrorMsg(null);

      return 'dev-local-token';
    }

    setAppCheckStatus('checking');
    setErrorMsg(null);
    try {
      const token = await getAppCheckToken();

      if (token) {
        setAppCheckToken(token);
        setAppCheckStatus('verified');

        return token;
      } else {
        setAppCheckToken(null);
        setAppCheckStatus('failed');

        return null;
      }
    } catch (err) {
      console.error('App Check verification failed:', err);
      setAppCheckToken(null);
      setAppCheckStatus('failed');

      return null;
    }
  }, []);

  const triggerVerificationIfNeeded = useCallback(() => {
    if (isProduction && appCheckStatus === 'idle') {
      verifyAppCheck();
    }
  }, [appCheckStatus, verifyAppCheck]);

  const isContactValid =
    contactMethod === 'email' ? isValidEmail(email) : isValidPhone(phone);

  const isFormValid =
    name.trim().length >= 2 &&
    isContactValid &&
    subject.trim().length >= 2 &&
    message.trim().length >= 5 &&
    terms;

  const canSubmit =
    isFormValid &&
    (isProduction ? appCheckStatus !== 'failed' : true) &&
    !isSubmitting;

  const handleSubmit = async (e?: FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!isFormValid) {
      if (contactMethod === 'email' && !isValidEmail(email)) {
        setErrorMsg('Podaj poprawny adres e-mail.');
      } else if (contactMethod === 'phone' && !isValidPhone(phone)) {
        setErrorMsg('Podaj poprawny numer telefonu (minimum 9 cyfr).');
      } else {
        setErrorMsg('Wypełnij poprawnie wszystkie wymagane pola.');
      }

      return;
    }

    setIsSubmitting(true);
    setErrorMsg(null);

    let token = appCheckToken;

    if (isProduction && (!token || appCheckStatus !== 'verified')) {
      token = await verifyAppCheck();
      if (!token) {
        setIsSubmitting(false);
        setErrorMsg(
          'Weryfikacja zabezpieczeń nie powiodła się. Spróbuj ponownie.'
        );

        return;
      }
    }

    try {
      const result = await sendContactMessage({
        name,
        contactMethod,
        phoneResponsePreference:
          contactMethod === 'phone' ? phoneResponsePreference : undefined,
        email: contactMethod === 'email' ? email : undefined,
        phone: contactMethod === 'phone' ? phone : undefined,
        subject,
        message,
        notificationPreferences: {
          email: contactMethod === 'email' || notifyPreferences.email,
          phone: contactMethod === 'phone' || notifyPreferences.phone,
        },
      });

      setCreatedTicket(result);
      setIsSuccess(true);
      setStatusMsg(
        contactMethod === 'email'
          ? 'Dziękujemy za kontakt! Wiadomość została przyjęta, a powiadomienie e-mail z bezpośrednim linkiem do wątku zostało wysłane.'
          : phoneResponsePreference === 'call'
            ? 'Dziękujemy za kontakt! Zgłoszenie telefoniczne zostało zarejestrowane. Pracownik serwisu oddzwoni do Ciebie na wskazany numer.'
            : 'Dziękujemy za kontakt! Wiadomość została przyjęta. Odpowiedź serwisu pojawi się w poniższym wątku w Twojej przeglądarce.'
      );
    } catch (err) {
      console.error('Błąd podczas wysyłania wiadomości:', err);
      setErrorMsg(
        'Wystąpił problem podczas wysyłania wiadomości. Spróbuj ponownie za chwilę.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setContactMethod('email');
    setPhoneResponsePreference('call');
    setSubject('');
    setMessage('');
    setTerms(false);
    setIsSuccess(false);
    setCreatedTicket(null);
    setStatusMsg(null);
    setErrorMsg(null);
    verifyAppCheck();
  };

  return {
    name,
    setName,
    contactMethod,
    setContactMethod,
    phoneResponsePreference,
    setPhoneResponsePreference,
    email,
    setEmail,
    phone,
    setPhone,
    subject,
    setSubject,
    message,
    setMessage,
    terms,
    setTerms,
    notifyPreferences,
    setNotifyPreferences,
    appCheckStatus,
    appCheckToken,
    verifyAppCheck,
    triggerVerificationIfNeeded,
    isSubmitting,
    isSuccess,
    createdTicket,
    statusMsg,
    errorMsg,
    isFormValid,
    canSubmit,
    handleSubmit,
    resetForm,
  };
}
